import React, {
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { ArrowClockwise, Clipboard, Printer } from "react-bootstrap-icons";
import FeedbackPanel from "./FeedbackPanel";
import ScoresTable from "./ScoresTable";
import ScoresVisibilityToggle from "./ScoresVisibilityToggle";
import { printReportNodes } from "./printReports";
import { updateEOYReport } from "../../services/APIClient";
import PrintHeader from "../PrintHeader";
import "../PrintStudentResults.css";

const SUMMARY_MODE_KEY_PREFIX = "eoy-report-assistant:summary-mode";

export function getSummaryStorageKey(
  teacherId,
  classId,
  report,
  prefix = SUMMARY_MODE_KEY_PREFIX
) {
  const teacherPart = teacherId || "unknown-teacher";
  const classPart = classId || "unknown-class";
  const studentPart = report?.student_id || report?.display_name || "unknown-student";
  return `${prefix}:${teacherPart}:${classPart}:${studentPart}`;
}

function getReportTimestamp(value) {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function isEditedReportCurrent(report) {
  if (report?.edited_report === undefined || report?.edited_report === null) {
    return false;
  }

  const editedAt = getReportTimestamp(report.edited_at);
  const generatedAt = getReportTimestamp(report.generated_at);

  if (!editedAt || !generatedAt) return true;

  return editedAt > generatedAt;
}

export function getSourceEoyReportType(kind) {
  if (kind === "teacher_edited") return "Teacher-edited report";
  if (kind === "ai_generated") return "AI-generated report";
  return "No matching report";
}

export function formatNoteType(noteType) {
  const normalized = String(noteType || "note")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const labels = {
    admin: "Admin",
    classroom_handover: "Classroom Observations",
    handover: "Classroom Observations",
    qr_code: "QR Code",
    qrcode: "QR Code",
    results: "Results",
    sen_watchlist: "SEN Watchlist",
    senwatchlist: "SEN Watchlist",
  };

  return (
    labels[normalized] ||
    normalized
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") ||
    "Note"
  );
}

function ApprovedNotesSection({
  approvedNotes,
  heading,
  description,
  studentId,
}) {
  return (
    <section className="nci-approved-notes" aria-label={heading}>
      <div className="nci-approved-notes-heading-row">
        <div>
          <h3>{heading}</h3>
          <p>{description}</p>
        </div>
        <span className="nci-approved-notes-count">
          {approvedNotes.length} note{approvedNotes.length === 1 ? "" : "s"}
        </span>
      </div>
      {approvedNotes.length > 0 ? (
        <div className="nci-approved-notes-list">
          {approvedNotes.map((note, index) => {
            const staffRole = String(note.staff_role || "").trim();
            const authorName = String(note.author_name || "").trim();
            return (
              <article
                className="nci-approved-note"
                key={note.note_id || `${studentId}-note-${index}`}
              >
                <div className="nci-approved-note-meta">
                  <span className="nci-approved-note-type">
                    {staffRole || formatNoteType(note.note_type)}
                  </span>
                  {authorName && (
                    <span className="nci-approved-note-author">
                      Recorded by {authorName}
                    </span>
                  )}
                  {note.date_created && (
                    <time className="nci-approved-note-date">
                      {note.date_created}
                    </time>
                  )}
                </div>
                <p>{note.text}</p>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="nci-approved-notes-empty">
          No staff observations were carried forward from the previous class for this pupil.
        </p>
      )}
    </section>
  );
}

const PTM_SECTION_DEFINITIONS = [
  { key: "doing_well", heading: "What your child is doing well" },
  { key: "learning_next", heading: "What your child is learning next" },
  { key: "help_at_home", heading: "Simple ways to help at home" },
];

export function getPTMReportSections(report, reportText = "") {
  const supplied = report?.report_sections || report?.reportSections;
  if (
    supplied &&
    PTM_SECTION_DEFINITIONS.every(({ key }) =>
      String(supplied[key] || "").trim(),
    )
  ) {
    return PTM_SECTION_DEFINITIONS.map(({ key, heading }) => ({
      key,
      heading,
      text: String(supplied[key]).trim(),
    }));
  }

  const cleaned = String(reportText || "")
    .replace(/^\s*#{1,6}\s*Parent Teacher Meeting(?: Handout)?\s*$/gim, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^\s*#{1,6}\s*/gm, "")
    .trim();
  const lower = cleaned.toLowerCase();
  const positions = PTM_SECTION_DEFINITIONS.map(({ heading }) =>
    lower.indexOf(heading.toLowerCase()),
  );
  if (positions.some((position) => position < 0)) return null;

  return PTM_SECTION_DEFINITIONS.map(({ key, heading }, index) => {
    const bodyStart = positions[index] + heading.length;
    const bodyEnd = positions[index + 1] ?? cleaned.length;
    return {
      key,
      heading,
      text: cleaned.slice(bodyStart, bodyEnd).replace(/^[\s:-]+/, "").trim(),
    };
  }).filter(({ text }) => Boolean(text));
}

function PTMReportSections({ sections }) {
  return (
    <div className="ptm-report-sections">
      {sections.map((section, index) => (
        <section className={`ptm-report-section ptm-report-section-${section.key}`} key={section.key}>
          <div className="ptm-report-section-number" aria-hidden="true">
            {index + 1}
          </div>
          <div>
            <h4>{section.heading}</h4>
            <p>{section.text}</p>
          </div>
        </section>
      ))}
    </div>
  );
}

function clearTimer(timerRef) {
  if (!timerRef.current) return;
  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

function applyClipboardScoreStyles(clone) {
  const assign = (node, styles) => {
    if (!node) return;
    Object.assign(node.style, styles);
  };

  assign(clone, {
    background: "white",
    color: "#1f2937",
    fontFamily: "Arial, sans-serif",
    fontSize: "16px",
    lineHeight: "1.6",
  });

  clone.querySelectorAll(".alpaca-print-header").forEach((node) =>
    assign(node, {
      display: "flex",
      alignItems: "center",
      marginBottom: "14px",
      paddingBottom: "10px",
      borderBottom: "2px solid #2c7693",
    })
  );

  clone.querySelectorAll(".printout-logo").forEach((node) =>
    assign(node, { display: "none" })
  );

  clone.querySelectorAll(".alpaca-print-header-text").forEach((node) =>
    assign(node, {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "16px",
    })
  );

  clone.querySelectorAll(
    ".alpaca-print-header-class, .alpaca-print-header-year"
  ).forEach((node) =>
    assign(node, {
      color: "#2c7693",
      fontSize: "12px",
      fontWeight: "700",
      letterSpacing: "1px",
    })
  );

  clone.querySelectorAll(".alpaca-print-header-powered").forEach((node) =>
    assign(node, {
      color: "#2c7693",
      fontSize: "11px",
      fontStyle: "italic",
      marginLeft: "16px",
    })
  );

  clone.querySelectorAll(".eoy-report-header").forEach((node) =>
    assign(node, {
      marginBottom: "14px",
    })
  );

  clone.querySelectorAll(".eoy-report-header strong").forEach((node) =>
    assign(node, {
      color: "#2b769d",
      fontSize: "18px",
      display: "block",
      marginBottom: "8px",
    })
  );

  clone.querySelectorAll(".eoy-report-header small").forEach((node) =>
    assign(node, {
      display: "block",
      marginBottom: "16px",
    })
  );

  clone.querySelectorAll(".eoy-scores-table-wrap").forEach((node) =>
    assign(node, {
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      marginBottom: "28px",
      padding: "18px 18px 22px",
    })
  );

  clone.querySelectorAll(".eoy-scores-table-header").forEach((node) =>
    assign(node, {
      color: "#6b7280",
      fontSize: "12px",
      fontWeight: "700",
      letterSpacing: "0.08em",
      marginBottom: "16px",
      textTransform: "uppercase",
    })
  );

  clone.querySelectorAll(".eoy-screener-section").forEach((node) =>
    assign(node, {
      borderBottom: "1px solid #f0f1f3",
      marginBottom: "26px",
      paddingBottom: "22px",
    })
  );

  const screenerSections = clone.querySelectorAll(".eoy-screener-section");
  const lastScreenerSection = screenerSections[screenerSections.length - 1];
  if (lastScreenerSection) {
    assign(lastScreenerSection, {
      borderBottom: "0",
      marginBottom: "0",
      paddingBottom: "0",
    });
  }

  clone.querySelectorAll(".eoy-screener-name-row").forEach((node) =>
    assign(node, {
      alignItems: "baseline",
      display: "flex",
      gap: "14px",
      marginBottom: "16px",
    })
  );

  clone.querySelectorAll(".eoy-screener-title").forEach((node) =>
    assign(node, {
      color: "#374151",
      fontSize: "15px",
      fontWeight: "700",
    })
  );

  clone.querySelectorAll(".eoy-screener-date").forEach((node) =>
    assign(node, {
      color: "#9ca3af",
      fontSize: "14px",
      whiteSpace: "nowrap",
    })
  );

  clone.querySelectorAll(".eoy-score-group-toggle").forEach((node) =>
    assign(node, {
      background: "transparent",
      border: "0",
      color: "inherit",
      display: "block",
      fontFamily: "Arial, sans-serif",
      padding: "0",
      textAlign: "center",
      width: "100%",
    })
  );

  clone.querySelectorAll(".eoy-scores-table-badge").forEach((node) =>
    assign(node, {
      alignItems: "center",
      border: "0",
      borderRadius: "5px",
      boxShadow: "none",
      color: "#111827",
      display: "inline-flex",
      fontSize: "14px",
      fontWeight: "700",
      height: "28px",
      justifyContent: "center",
      lineHeight: "1",
      minWidth: "54px",
      padding: "0 10px",
      textAlign: "center",
    })
  );

  clone.querySelectorAll(".eoy-parent-report-section").forEach((node) =>
    assign(node, {
      borderTop: "none",
      marginTop: "26px",
      paddingTop: "0",
    })
  );

  clone.querySelectorAll(".eoy-report-print-summary").forEach((node) =>
    assign(node, {
      color: "#1f2937",
      display: "block",
      fontSize: "16px",
      lineHeight: "1.7",
      marginTop: "0",
      whiteSpace: "pre-wrap",
    })
  );
}

function convertClipboardScoreGridsToTables(clone) {
  const grids = [
    ...clone.querySelectorAll(".eoy-task-badges"),
    ...clone.querySelectorAll(".eoy-category-task-badges"),
  ];

  grids.forEach((grid) => {
    const tasks = Array.from(grid.querySelectorAll(":scope > .eoy-task-badge-wrap"));
    if (tasks.length === 0) return;

    const table = document.createElement("table");
    table.setAttribute("role", "presentation");
    table.style.borderCollapse = "collapse";
    table.style.tableLayout = "fixed";
    table.style.width = "100%";

    const labelsRow = document.createElement("tr");
    const scoresRow = document.createElement("tr");

    tasks.forEach((task) => {
      const label = task.querySelector(".eoy-task-label");
      const badge = task.querySelector(".eoy-scores-table-badge");
      const labelCell = document.createElement("td");
      const scoreCell = document.createElement("td");

      labelCell.style.color = "#6b7280";
      labelCell.style.fontSize = "12px";
      labelCell.style.fontWeight = "600";
      labelCell.style.lineHeight = "1.25";
      labelCell.style.padding = "6px 7px 10px";
      labelCell.style.textAlign = "center";
      labelCell.style.verticalAlign = "bottom";
      labelCell.style.width = `${100 / tasks.length}%`;
      labelCell.textContent = label?.textContent || "";

      scoreCell.style.padding = "0 7px 10px";
      scoreCell.style.textAlign = "center";
      scoreCell.style.verticalAlign = "top";
      scoreCell.style.width = `${100 / tasks.length}%`;
      if (badge) scoreCell.appendChild(badge.cloneNode(true));

      labelsRow.appendChild(labelCell);
      scoresRow.appendChild(scoreCell);
    });

    table.appendChild(labelsRow);
    table.appendChild(scoresRow);
    const spacerRow = document.createElement("tr");
    const spacerCell = document.createElement("td");
    spacerCell.colSpan = tasks.length;
    spacerCell.innerHTML = "&nbsp;";
    spacerCell.style.fontSize = "1px";
    spacerCell.style.height = "18px";
    spacerCell.style.lineHeight = "18px";
    spacerCell.style.padding = "0";
    spacerRow.appendChild(spacerCell);
    table.appendChild(spacerRow);
    grid.replaceWith(table);
  });
}

function createClipboardSpacer(height) {
  const spacer = document.createElement("div");
  spacer.innerHTML = "&nbsp;";
  spacer.style.fontSize = "1px";
  spacer.style.height = `${height}px`;
  spacer.style.lineHeight = `${height}px`;
  return spacer;
}

function addClipboardVerticalSpacers(clone) {
  const screenerSections = Array.from(clone.querySelectorAll(".eoy-screener-section"));
  screenerSections.slice(0, -1).forEach((section) => {
    section.after(createClipboardSpacer(18));
  });

  const parentReportSection = clone.querySelector(".eoy-parent-report-section");
  if (parentReportSection) {
    parentReportSection.before(createClipboardSpacer(24));
  }
}

function addClipboardScoreHeaderSpacing(clone) {
  clone.querySelectorAll(".eoy-screener-name-row").forEach((row) => {
    const title = row.querySelector(".eoy-screener-title");
    const date = row.querySelector(".eoy-screener-date");
    if (title && date && title.nextSibling === date) {
      const spacer = document.createTextNode("  ");
      title.after(spacer);
    }
  });
}

function addClipboardHeaderLineBreaks(clone) {
  clone.querySelectorAll(".eoy-scores-table-header, .eoy-report-header").forEach((header) => {
    const spacer = document.createElement("br");
    header.after(spacer);
  });
  
}

function convertClipboardSummaryLineBreaks(clone) {
  clone.querySelectorAll(".eoy-report-print-summary").forEach((node) => {
    const lines = node.textContent.split("\n");
    node.textContent = "";
    lines.forEach((line, index) => {
      if (index > 0) {
        node.appendChild(document.createElement("br"));
      }
      node.appendChild(document.createTextNode(line));
    });
  });
}

export function buildClipboardPayload(reportNode) {
  const reportCard = reportNode.querySelector(".eoy-report-card");
  if (!reportCard) return null;

  const clone = reportCard.cloneNode(true);
  clone.querySelectorAll(".no-print").forEach((node) => node.remove());
  clone.querySelectorAll(".eoy-screener-section-closed").forEach((node) => node.remove());
  clone.querySelectorAll(
    ".eoy-screener-toggle-row, .eoy-expand-btn, .eoy-expand-all-categories-btn, .eoy-section-chevron, .eoy-score-group-plus"
  ).forEach((node) => node.remove());
  clone.querySelectorAll(".eoy-report-textarea").forEach((node) => node.remove());
  clone.querySelectorAll(".eoy-report-print-summary").forEach((node) => {
    node.removeAttribute("aria-hidden");
    node.style.display = "block";
    node.style.whiteSpace = "pre-wrap";
  });
  applyClipboardScoreStyles(clone);
  convertClipboardScoreGridsToTables(clone);
  addClipboardHeaderLineBreaks(clone);
  addClipboardScoreHeaderSpacing(clone);
  addClipboardVerticalSpacers(clone);
  convertClipboardSummaryLineBreaks(clone);

  return {
    html: clone.outerHTML,
    text: clone.innerText.replace(/\n{3,}/g, "\n\n").trim(),
  };
}

export async function writeClipboard(payload) {
  if (!payload) return false;

  if (navigator.clipboard?.write && window.ClipboardItem) {
    const item = new window.ClipboardItem({
      "text/html": new Blob([payload.html], { type: "text/html" }),
      "text/plain": new Blob([payload.text], { type: "text/plain" }),
    });
    await navigator.clipboard.write([item]);
    return true;
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(payload.text);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = payload.text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  return copied;
}

/**
 * One student's EOY report card.
 *
 * Shows:
 *   1. Student name + generation time
 *   2. ScoresTable - visible screening points
 *   3. "This is the report you can share with the parents" label (hidden on print)
 *   4. Editable report text (teacher can adjust before printing/exporting)
 *   5. Feedback (thumbs up/down, hidden on print)
 */
function ReportCard({
  report,
  classId,
  className,
  summaryMode,
  onSummaryModeChange,
  showScores = true,
  onScoresVisibilityChange,
  academicYear = "",
  expandAllCategories = false,
  onReportNodeReady,
  onRegenerateReport,
  regenerating,
  updateReport = updateEOYReport,
  assessmentName = "End of Year Report",
  scoresHeading = "Assessment results the parent report is based on",
  showSourceEoyReport = false,
  sourceEoyHeading = "Previous End of Year report",
  sourceEoyDescription = "The previous report used as source evidence for this insight.",
  showApprovedNotes = false,
  approvedNotesHeading = "Observations from the previous year",
  approvedNotesDescription = "Approved teacher notes used to create this insight.",
  notesAfterReport = false,
  reportHeading = "",
  reportHeadingDescription = "",
  editableLabel = "REPORT CAN BE EDITED AND SHARED WITH PARENTS. EDITS ARE SAVED AUTOMATICALLY",
  showFeedback = true,
  readOnly = false,
  reportLayout = "default",
  printLayout = "default",
}) {
  const [editedText, setEditedText] = useState("");
  const [localSummaryMode, setLocalSummaryMode] = useState("generated");
  const [saveStatus, setSaveStatus] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [hasSavedEditedSummary, setHasSavedEditedSummary] = useState(false);
  const reportShellRef = useRef(null);
  const textareaRef = useRef(null);
  const activePrintCleanupRef = useRef(null);
  const saveStatusTimer = useRef(null);
  const pendingSaveTimer = useRef(null);
  const copyStatusTimer = useRef(null);
  const saveRequestIdRef = useRef(0);
  const saveChainRef = useRef(Promise.resolve());
  const latestEditedTextRef = useRef("");
  const mountedRef = useRef(true);

  const generatedText = report?.report || "";
  const savedEditedText = report?.edited_report || "";
  const hasCurrentEditedText = isEditedReportCurrent(report);
  const currentEditedText = hasCurrentEditedText ? savedEditedText : "";
  const studentId = report?.student_id;
  const ptmSections =
    reportLayout === "ptm-sections"
      ? getPTMReportSections(report, generatedText)
      : null;
  const isParentHandout = printLayout === "parent-handout";

  const queueReportSave = useCallback((nextText) => {
    if (!studentId) return Promise.resolve();

    const saveTask = saveChainRef.current
      .catch(() => {})
      .then(() => updateReport(studentId, nextText, report));

    saveChainRef.current = saveTask.catch(() => {});
    return saveTask;
  }, [report, studentId, updateReport]);

  function scheduleSaveStatusClear() {
    clearTimer(saveStatusTimer);
    saveStatusTimer.current = window.setTimeout(() => {
      setSaveStatus("");
      saveStatusTimer.current = null;
    }, 1800);
  }

  async function persistReportEdit(nextText) {
    const saveRequestId = saveRequestIdRef.current + 1;
    saveRequestIdRef.current = saveRequestId;

    try {
      await queueReportSave(nextText);
      if (!mountedRef.current || saveRequestIdRef.current !== saveRequestId) return;
      setSaveStatus("Saved");
    } catch (err) {
      if (!mountedRef.current || saveRequestIdRef.current !== saveRequestId) return;
      console.warn("Could not save EOY report edit:", err);
      setSaveStatus("Save failed");
    }

    scheduleSaveStatusClear();
  }

  // Initialise text when report loads
  useEffect(() => {
    if (generatedText) {
      setEditedText(hasCurrentEditedText ? currentEditedText : generatedText);
      latestEditedTextRef.current = hasCurrentEditedText
        ? currentEditedText
        : generatedText;
      setLocalSummaryMode(hasCurrentEditedText ? "edited" : "generated");
      setHasSavedEditedSummary(hasCurrentEditedText);
      setSaveStatus("");
    }
  }, [currentEditedText, generatedText, hasCurrentEditedText]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (pendingSaveTimer.current) {
        clearTimer(pendingSaveTimer);
        queueReportSave(latestEditedTextRef.current).catch((err) => {
          console.warn("Could not save pending EOY report edit:", err);
        });
      }
      clearTimer(saveStatusTimer);
      clearTimer(copyStatusTimer);
      activePrintCleanupRef.current?.();
    };
  }, [queueReportSave, studentId]);

  useEffect(() => {
    if (!report?.success || !report?.student_id || !onReportNodeReady) return undefined;

    onReportNodeReady(report.student_id, reportShellRef.current);
    return () => onReportNodeReady(report.student_id, null);
  }, [onReportNodeReady, report?.student_id, report?.success]);

  const activeSummaryMode = summaryMode || localSummaryMode;
  const visibleSummary =
    activeSummaryMode === "generated" ? generatedText : editedText;

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    resizeTextarea();
  }, [visibleSummary, activeSummaryMode]);

  if (!report) return null;

  const displayId = report.display_name || report.student_id;
  const isRegenerationAvailable = report.can_regenerate === true;

  // Error state
  if (!report.success) {
    return (
      <div className="eoy-report-card eoy-report-card-error">
        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          <span className="eoy-report-badge eoy-report-badge-error">
            {report.unavailable
              ? "Not available"
              : isRegenerationAvailable
              ? "Update available"
              : "Failed"}
          </span>
        </div>
        <p className="eoy-report-text">
          {report.error || "Report could not be generated."}
        </p>
        {onRegenerateReport && report.student_id && (
          <button
            aria-label={`Regenerate report for ${displayId}`}
            className="eoy-secondary-btn eoy-regenerate-report-btn"
            data-tooltip={`Regenerate report for ${displayId}`}
            disabled={regenerating}
            onClick={() => onRegenerateReport(report)}
            type="button"
          >
            <ArrowClockwise aria-hidden="true" size={15} />
            {regenerating ? "Regenerating..." : "Regenerate report"}
          </button>
        )}
      </div>
    );
  }

  // Get screeners from backend response
  const screeners = report.scores || [];
  const approvedNotes = Array.isArray(report.approved_notes)
    ? report.approved_notes
    : [];
  const sourceEoyReport =
    typeof report.source_eoy_report === "string"
      ? report.source_eoy_report.trim()
      : "";
  const sourceEoyType = getSourceEoyReportType(report.source_eoy_report_kind);

  const handleTextChange = (e) => {
    const nextText = e.target.value;
    setEditedText(nextText);
    latestEditedTextRef.current = nextText;
    resizeTextarea();
    setHasSavedEditedSummary(true);
    setSaveStatus("Saving...");
    clearTimer(saveStatusTimer);
    clearTimer(pendingSaveTimer);

    pendingSaveTimer.current = window.setTimeout(() => {
      pendingSaveTimer.current = null;
      persistReportEdit(nextText);
    }, 600);
  };

  const handleTextBlur = () => {
    if (!pendingSaveTimer.current) return;

    clearTimer(pendingSaveTimer);
    persistReportEdit(latestEditedTextRef.current);
  };

  const handleSummaryModeChange = (nextSummaryMode) => {
    setLocalSummaryMode(nextSummaryMode);
    onSummaryModeChange?.(nextSummaryMode);
  };

  const handlePrintReport = () => {
    const reportNode = reportShellRef.current;
    if (!reportNode) return;

    activePrintCleanupRef.current?.();
    activePrintCleanupRef.current = printReportNodes([reportNode]);
  };

  const handleCopyReport = async () => {
    const reportNode = reportShellRef.current;
    if (!reportNode) return;

    try {
      const copied = await writeClipboard(buildClipboardPayload(reportNode));
      setCopyStatus(copied ? "Copied" : "Copy failed");
    } catch (err) {
      console.warn("Could not copy EOY report:", err);
      setCopyStatus("Copy failed");
    }

    clearTimer(copyStatusTimer);
    copyStatusTimer.current = window.setTimeout(() => {
      setCopyStatus("");
      copyStatusTimer.current = null;
    }, 1800);
  };

  return (
    <div
      className={`eoy-report-print-shell${
        isParentHandout ? " eoy-report-print-shell-parent-handout" : ""
      }`}
      ref={reportShellRef}
    >
      <div className="eoy-report-card">
        <PrintHeader
          className={className}
          academicYear={academicYear}
          assessmentName={assessmentName}
        />
        <div className="eoy-report-toolbar no-print">
          <span
            className={`eoy-copy-status ${
              copyStatus ? "" : "eoy-copy-status-hidden"
            }`}
            aria-live="polite"
          >
            {copyStatus || "Copied"}
          </span>
          {screeners.length > 0 && (
            <ScoresVisibilityToggle
              checked={showScores}
              tooltip="Show or hide assessment results in this report"
              onChange={onScoresVisibilityChange}
            />
          )}
          <button
            aria-label="Copy report to clipboard"
            className="eoy-toolbar-icon-btn"
            onClick={handleCopyReport}
            data-tooltip="Copy report to clipboard"
            type="button"
          >
            <Clipboard aria-hidden="true" size={16} />
          </button>
          <button
            aria-label="Print this report"
            className="eoy-toolbar-icon-btn"
            onClick={handlePrintReport}
            data-tooltip="Print this report"
            type="button"
          >
            <Printer aria-hidden="true" size={16} />
          </button>
        </div>

        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          {report.elapsed_seconds && (
            <span className="eoy-report-meta no-print">
              generated in {report.elapsed_seconds}s
            </span>
          )}
        </div>

        {isParentHandout && (
          <div className="ptm-print-introduction print-only">
            <h1>Parent Teacher Meeting</h1>
            <p>A short summary to support your conversation with the teacher.</p>
          </div>
        )}

        {/* Scores table - visible scores print with the report */}
        <ScoresTable
          screeners={screeners}
          showScores={showScores}
          forceExpandAll={expandAllCategories}
          heading={scoresHeading}
        />

        {showSourceEoyReport && (
          <section className="nci-source-eoy" aria-label={sourceEoyHeading}>
            <div className="nci-source-eoy-heading-row">
              <div>
                <h3>{sourceEoyHeading}</h3>
                <p>{sourceEoyDescription}</p>
              </div>
              <span className="nci-source-eoy-type">{sourceEoyType}</span>
            </div>
            {sourceEoyReport ? (
              <p className="nci-source-eoy-text">{sourceEoyReport}</p>
            ) : (
              <p className="nci-source-eoy-empty">
                No previous End of Year report matching this academic year was available.
              </p>
            )}
          </section>
        )}

        {showApprovedNotes && !notesAfterReport && (
          <ApprovedNotesSection
            approvedNotes={approvedNotes}
            heading={approvedNotesHeading}
            description={approvedNotesDescription}
            studentId={studentId}
          />
        )}

        {/* Parent report section - editable */}
        <div className="eoy-parent-report-section">
          {reportHeading && (
            <div className="nci-learning-overview-heading">
              <h3>{reportHeading}</h3>
              {reportHeadingDescription && <p>{reportHeadingDescription}</p>}
            </div>
          )}
          <div className="eoy-parent-report-label no-print">
            {editableLabel}
            {!readOnly && hasSavedEditedSummary && (
              <span className="eoy-edited-badge">Edited</span>
            )}
          </div>
          {!readOnly && <div className="eoy-summary-toggle-row no-print">
            <span className="eoy-summary-toggle-label">AI Generated</span>
            <label
              className="eoy-summary-switch"
              data-tooltip="Toggle between generated and edited summary"
            >
              <input
                type="checkbox"
                aria-label="Toggle between generated and edited summary"
                checked={activeSummaryMode === "edited"}
                onChange={(e) =>
                  handleSummaryModeChange(
                    e.target.checked ? "edited" : "generated"
                  )
                }
              />
              <span className="eoy-summary-slider" />
            </label>
            <span className="eoy-summary-toggle-label">
              Edited
            </span>
          </div>}
          {readOnly ? (
            ptmSections ? (
              <PTMReportSections sections={ptmSections} />
            ) : (
              <p className="eoy-report-read-only-text">{visibleSummary}</p>
            )
          ) : (
            <textarea
              ref={textareaRef}
              className="eoy-report-textarea"
              value={visibleSummary}
              onChange={handleTextChange}
              onBlur={handleTextBlur}
              readOnly={activeSummaryMode === "generated"}
              rows={6}
            />
          )}
          {(!ptmSections || !readOnly) && (
            <div className="eoy-report-print-summary" aria-hidden="true">
              {visibleSummary}
            </div>
          )}
          {!readOnly && <div className="eoy-summary-status-row no-print" aria-live="polite">
            {activeSummaryMode === "generated" && (
              <span className="eoy-summary-help">
                Switch to Edited to make changes.
              </span>
            )}
            {activeSummaryMode === "edited" && (
              <span
                className={`eoy-save-status ${
                  saveStatus ? "" : "eoy-save-status-hidden"
                }`}
              >
                {saveStatus || "Saved"}
              </span>
            )}
          </div>}
        </div>

        {showApprovedNotes && notesAfterReport && (
          <ApprovedNotesSection
            approvedNotes={approvedNotes}
            heading={approvedNotesHeading}
            description={approvedNotesDescription}
            studentId={studentId}
          />
        )}

        {/* Feedback - hidden on print */}
        {showFeedback && !readOnly && <div className="no-print">
          <FeedbackPanel
            studentId={report.student_id}
            reportText={visibleSummary}
            classId={classId}
            className={className}
          />
        </div>}

        {isParentHandout && (
          <footer className="ptm-print-footer print-only">
            Please speak with your child&apos;s teacher if you would like to discuss any part of this handout.
          </footer>
        )}
      </div>
    </div>
  );
}

export default ReportCard;
